(() => {
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  function init(scope = document) {
    scope.querySelectorAll('[data-fh-hero]:not([data-fh-ready])').forEach(hero => {
      hero.dataset.fhReady = 'true';
      const track = hero.querySelector('.fh-hero-track');
      const links = [...hero.querySelectorAll('[data-fh-slide]')];
      const slides = [...hero.querySelectorAll('.fh-hero-slide')];
      const toggle = hero.querySelector('[data-fh-autoplay-toggle]');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const interval = Number(hero.dataset.fhInterval) || 7000;
      let paused = hero.dataset.fhAutoplay !== 'true' || reducedMotion.matches;
      let hovering = false;
      let visible = false;
      let timer;
      let frame;
      const index = () => track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
      const schedule = () => {
        clearTimeout(timer);
        if (paused || hovering || !visible || document.hidden || slides.length < 2) return;
        timer = setTimeout(() => {
          track.scrollTo({ left: track.clientWidth * ((index() + 1) % slides.length), behavior: motion() });
          schedule();
        }, interval);
      };
      const setPaused = value => {
        paused = value;
        if (toggle) {
          const label = paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
          toggle.querySelector('[data-fh-autoplay-label]').textContent = label;
          toggle.setAttribute('aria-label', label);
          toggle.querySelector('[data-fh-pause-icon]').toggleAttribute('hidden', paused);
          toggle.querySelector('[data-fh-play-icon]').toggleAttribute('hidden', !paused);
        }
        track.setAttribute('aria-live', paused ? 'polite' : 'off');
        schedule();
      };
      const sync = () => {
        const active = index();
        links.forEach((link, i) => i === active ? link.setAttribute('aria-current', 'true') : link.removeAttribute('aria-current'));
        slides.forEach((slide, i) => { slide.inert = i !== active; });
      };
      links.forEach((link, i) => link.addEventListener('click', e => {
        e.preventDefault();
        setPaused(true);
        track.scrollTo({ left: track.clientWidth * i, behavior: motion() });
      }, options));
      track.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(sync); }, { ...options, passive: true });
      track.addEventListener('keydown', e => {
        if (e.target !== track || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        e.preventDefault();
        setPaused(true);
        track.scrollBy({ left: track.clientWidth * (e.key === 'ArrowRight' ? 1 : -1), behavior: motion() });
      }, options);
      track.addEventListener('pointerdown', () => setPaused(true), options);
      track.addEventListener('wheel', e => { if (Math.abs(e.deltaX) > 0) setPaused(true); }, { ...options, passive: true });
      hero.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { hovering = true; schedule(); } }, options);
      hero.addEventListener('pointerleave', () => { hovering = false; schedule(); }, options);
      hero.addEventListener('focusin', e => { if (!toggle?.contains(e.target)) setPaused(true); }, options);
      if (toggle && slides.length > 1) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => setPaused(!paused), options);
      }
      document.addEventListener('visibilitychange', schedule, options);
      reducedMotion.addEventListener('change', e => { if (e.matches) setPaused(true); }, options);
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); }, { threshold: 0.15 });
      observer.observe(hero);
      const resize = new ResizeObserver(sync);
      resize.observe(track);
      hero.addEventListener('shopify:block:select', e => {
        setPaused(true);
        const slide = e.target.closest('.fh-hero-slide');
        if (slide) track.scrollTo({left: slide.offsetLeft - track.offsetLeft, behavior: 'instant'});
      }, options);
      document.addEventListener('shopify:section:unload', e => {
        if (!e.target.contains(hero)) return;
        clearTimeout(timer);
        cancelAnimationFrame(frame);
        observer.disconnect();
        resize.disconnect();
        controller.abort();
      }, options);
      sync();
      setPaused(paused);
    });
    scope.querySelectorAll('[data-fh-tabs]:not([data-fh-ready])').forEach(root => {
      root.dataset.fhReady = 'true';
      const tabs = [...root.querySelectorAll('[data-fh-tab]')];
      const panels = [...root.querySelectorAll('[data-fh-panel]')];
      root.querySelector('[data-fh-tablist]').setAttribute('role', 'tablist');
      root.classList.add('fh-tabs-ready');
      const select = i => {
        tabs.forEach((tab, n) => {
          tab.setAttribute('role', 'tab'); tab.setAttribute('aria-controls', panels[n].id);
          tab.setAttribute('aria-selected', String(i === n)); tab.tabIndex = i === n ? 0 : -1;
          panels[n].setAttribute('role', 'tabpanel'); panels[n].setAttribute('aria-labelledby', tab.id);
          panels[n].tabIndex = 0; panels[n].hidden = i !== n;
          panels[n].dispatchEvent(new Event('fh:shown'));
        });
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', e => { e.preventDefault(); select(i); });
        tab.addEventListener('keydown', e => {
          let next;
          if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { e.preventDefault(); select(next); tabs[next].focus(); }
        });
      });
      panels.forEach((panel, i) => {
        const controls = panel.querySelector('[data-fh-controls]');
        if (!controls) return;
        const track = panel.querySelector('.fh-product-track');
        const previous = controls.querySelector('[data-fh-prev]');
        const next = controls.querySelector('[data-fh-next]');
        const update = () => {
          if (panel.hidden) return;
          controls.hidden = track.scrollWidth <= track.clientWidth + 2;
          previous.disabled = track.scrollLeft <= 2;
          next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        };
        previous.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * .8, behavior: motion() }));
        next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * .8, behavior: motion() }));
        track.addEventListener('scroll', update, { passive: true });
        panel.addEventListener('fh:shown', update);
        panel.addEventListener('shopify:block:select', () => select(i));
        new ResizeObserver(update).observe(track);
      });
      select(0);
    });
    scope.querySelectorAll('[data-fh-compare]:not([data-fh-ready])').forEach(root => {
      root.dataset.fhReady = 'true';
      const input = root.querySelector('[data-fh-range]');
      const update = () => {
        root.style.setProperty('--fh-position', input.value + '%');
        input.setAttribute('aria-valuetext', input.value + '% ' + input.dataset.afterLabel);
      };
      input.addEventListener('input', update);
      update();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();

